import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { useStatusTracksByConfiguration } from '@/features/contract/statuses/hooks'
import { useDocumentTypes } from '@/features/admin/document-types/hooks'
import { useFormSchema } from '@/features/admin/forms/hooks'
import { useCreateAnnexRequirement } from '@/features/contract/gating/annexHooks'
import type { RequirementKind } from '@/features/contract/gating/types'
import { formatError } from '@/features/admin/stages/components/formatError'
import { KindButton } from './KindButton'
import { Field } from './Field'

/** Dialog tworzenia warunku publikacji aneksu (DRAFT -> PUBLISHED). */
export function AnnexRequirementFormDialog({
  configurationCode,
  groupNo,
  onClose,
}: {
  configurationCode: string
  groupNo: number
  onClose: () => void
}) {
  const tracks = useStatusTracksByConfiguration(configurationCode, true)
  const documentTypes = useDocumentTypes(configurationCode, true)
  const annexSchema = useFormSchema('annex')
  const create = useCreateAnnexRequirement(configurationCode)

  const [kind, setKind] = useState<RequirementKind>('STATUS')
  const [trackId, setTrackId] = useState<number | ''>('')
  const [requiredValueId, setRequiredValueId] = useState<number | ''>('')
  const [documentTypeId, setDocumentTypeId] = useState<number | ''>('')
  const [fieldCode, setFieldCode] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const selectedTrack = tracks.data?.find(t => t.id === trackId)

  const submit = () => {
    setError(null)
    if (kind === 'STATUS') {
      if (!trackId || !requiredValueId) { setError('Wybierz tor i wartość.'); return }
      create.mutate(
        {
          kind: 'STATUS',
          trackId: Number(trackId),
          requiredValueId: Number(requiredValueId),
          groupNo,
          sortOrder: 100,
        },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
      return
    }
    if (kind === 'DOCUMENT') {
      if (!documentTypeId) { setError('Wybierz typ dokumentu.'); return }
      create.mutate(
        {
          kind: 'DOCUMENT',
          documentTypeId: Number(documentTypeId),
          groupNo,
          sortOrder: 100,
        },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
      return
    }
    // FIELD
    if (!fieldCode) { setError('Wybierz pole.'); return }
    create.mutate(
      {
        kind: 'FIELD',
        fieldCode,
        groupNo,
        sortOrder: 100,
      },
      { onSuccess: onClose, onError: e => setError(formatError(e)) },
    )
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[480px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Nowy warunek publikacji aneksu</Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            DRAFT → PUBLISHED · Grupa <strong>{groupNo}</strong>
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <KindButton active={kind === 'STATUS'} label="Status" onClick={() => setKind('STATUS')} />
              <KindButton active={kind === 'DOCUMENT'} label="Dokument" onClick={() => setKind('DOCUMENT')} />
              <KindButton active={kind === 'FIELD'} label="Pole" onClick={() => setKind('FIELD')} />
            </div>

            {kind === 'STATUS' && (
              <>
                <Field label="Tor">
                  <Combobox<number>
                    value={trackId === '' ? null : trackId}
                    onChange={v => { setTrackId(v ?? ''); setRequiredValueId('') }}
                    options={(tracks.data ?? []).map(t => ({ value: t.id, label: t.labelPl }))}
                    emptyOptionLabel="- wybierz -"
                    placeholder="Wybierz tor..."
                  />
                </Field>
                {selectedTrack && (
                  <Field label="Wartość">
                    <Combobox<number>
                      value={requiredValueId === '' ? null : requiredValueId}
                      onChange={v => setRequiredValueId(v ?? '')}
                      options={selectedTrack.values.map(v => ({ value: v.id, label: v.labelPl }))}
                      emptyOptionLabel="- wybierz -"
                      placeholder="Wybierz wartość..."
                    />
                  </Field>
                )}
              </>
            )}

            {kind === 'DOCUMENT' && (
              <Field label="Typ dokumentu">
                <Combobox<number>
                  value={documentTypeId === '' ? null : documentTypeId}
                  onChange={v => setDocumentTypeId(v ?? '')}
                  options={(documentTypes.data ?? []).map(t => ({ value: t.id, label: t.labelPl }))}
                  emptyOptionLabel="- wybierz -"
                  placeholder="Wybierz typ dokumentu..."
                />
              </Field>
            )}

            {kind === 'FIELD' && (
              <Field label="Pole aneksu">
                <Combobox<string>
                  value={fieldCode || null}
                  onChange={v => setFieldCode(v ?? '')}
                  options={(annexSchema.data?.fields ?? []).map(f => ({ value: f.fieldCode, label: f.labelPl }))}
                  emptyOptionLabel="- wybierz -"
                  placeholder="Wybierz pole..."
                />
              </Field>
            )}

            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 border rounded"
                disabled={create.isPending}
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={submit}
                className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50"
                disabled={create.isPending}
              >
                {create.isPending ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
